import React from 'react';
import { createRoot } from 'react-dom/client';
import UFOVGame, {
    loadUFOVProgress,
    resetUFOVProgress,
} from './components/UFOVGame';

let UFOVHost = null;
let UFOVRoot = null;
let UFOVRootHost = null;
let UFOVViewportHeight = 0;
let UFOVViewportHeightRaf = 0;
let UFOVViewportHeightTimers = [];
let UFOVViewportHeightSyncStarted = false;

const getViewportHeight = () => {
    const visualViewport = window.visualViewport;
    const visualViewportHeight = visualViewport
        && (!visualViewport.scale || Math.abs(visualViewport.scale - 1) < 0.01)
        ? visualViewport.height
        : 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || visualViewportHeight;

    return Math.max(320, Math.round(viewportHeight || 0));
};

const syncViewportHeight = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    const viewportHeight = getViewportHeight();

    if (!viewportHeight || viewportHeight === UFOVViewportHeight) {
        return;
    }

    UFOVViewportHeight = viewportHeight;
    document.documentElement.style.setProperty('--UFOV-app-height', `${viewportHeight}px`);
};

const scheduleViewportHeightSync = () => {
    if (UFOVViewportHeightRaf) {
        window.cancelAnimationFrame(UFOVViewportHeightRaf);
    }

    UFOVViewportHeightTimers.forEach((timer) => window.clearTimeout(timer));
    UFOVViewportHeightTimers = [];

    UFOVViewportHeightRaf = window.requestAnimationFrame(() => {
        UFOVViewportHeightRaf = 0;
        syncViewportHeight();
    });

    UFOVViewportHeightTimers = [
        window.setTimeout(syncViewportHeight, 120),
        window.setTimeout(syncViewportHeight, 360),
    ];
};

const startViewportHeightSync = () => {
    if (UFOVViewportHeightSyncStarted) {
        syncViewportHeight();
        return;
    }

    UFOVViewportHeightSyncStarted = true;
    syncViewportHeight();
    window.addEventListener('resize', scheduleViewportHeightSync, { passive: true });
    window.addEventListener('orientationchange', scheduleViewportHeightSync, { passive: true });
    document.addEventListener('visibilitychange', scheduleViewportHeightSync);

    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', scheduleViewportHeightSync, { passive: true });
    }

    window.setInterval(syncViewportHeight, 1000);

    if (window.screen?.orientation?.addEventListener) {
        window.screen.orientation.addEventListener('change', scheduleViewportHeightSync);
    }

    if (window.matchMedia) {
        const orientationQuery = window.matchMedia('(orientation: portrait)');

        if (orientationQuery.addEventListener) {
            orientationQuery.addEventListener('change', scheduleViewportHeightSync);
        } else if (orientationQuery.addListener) {
            orientationQuery.addListener(scheduleViewportHeightSync);
        }
    }
};

const ensureHost = () => {
    if (UFOVHost && document.body.contains(UFOVHost)) {
        return UFOVHost;
    }

    UFOVHost = document.createElement('div');
    UFOVHost.id = 'UFOV-game-root';
    document.body.appendChild(UFOVHost);

    return UFOVHost;
};

const renderGame = (host, props = {}) => {
    if (!UFOVRoot || UFOVRootHost !== host) {
        if (UFOVRoot) {
            UFOVRoot.unmount();
        }

        UFOVRoot = createRoot(host);
        UFOVRootHost = host;
    }

    UFOVRoot.render(<UFOVGame {...props} />);
};

const closeGame = () => {
    if (UFOVRoot) {
        UFOVRoot.unmount();
        UFOVRoot = null;
        UFOVRootHost = null;
    }

    if (UFOVHost) {
        UFOVHost.remove();
        UFOVHost = null;
    }

    document.documentElement.classList.remove('UFOV-game-is-open');
    document.body.classList.remove('UFOV-game-is-open');
};

const openGame = () => {
    const host = ensureHost();

    syncViewportHeight();
    document.documentElement.classList.add('UFOV-game-is-open');
    document.body.classList.add('UFOV-game-is-open');
    renderGame(host, { onClose: closeGame });
};

const autoMountEmbeddedGame = () => {
    const embeddedHost = document.querySelector('[data-ufov-game-root]');

    if (!embeddedHost) {
        return;
    }

    syncViewportHeight();
    renderGame(embeddedHost, { embedded: true });
};

if (typeof window !== 'undefined') {
    startViewportHeightSync();

    window.OpenGame = openGame;
    window.openUFOVGame = openGame;
    window.closeUFOVGame = closeGame;
    window.UFOVGame = {
        open: openGame,
        close: closeGame,
        getProgress: loadUFOVProgress,
        resetProgress: resetUFOVProgress,
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', autoMountEmbeddedGame, { once: true });
    } else {
        autoMountEmbeddedGame();
    }
}
